<script setup lang="ts">
/** Standing questions from the CMS (`GET /api/cms/faq`) as kit rows that open in place: a native `<details>`, so the keyboard works for free. Draws nothing when the CMS has none. */
import { useI18n } from '@/i18n';
import type { FaqEntry } from '@/net/api';
import GameIcon from '@/ui/kit/GameIcon.vue';
import { sanitizeHtml } from '@/ui/sanitize-html';

import SectionShell from './SectionShell.vue';

defineProps<{ entries: readonly FaqEntry[] }>();
const { t } = useI18n();
</script>

<template>
  <SectionShell v-if="entries.length" id="faq" icon="lighthouse" :title="t('landing.faq.caption')" backdrop="/art/scenes/record.webp">
    <div class="list">
      <details v-for="(f, i) in entries" :key="f.id" class="q k-well" :open="i === 0" data-slam="rise" :style="{ '--i': i }">
        <summary class="ask">
          <GameIcon name="lighthouse" :size="44" />
          <span class="k-label">{{ f.question }}</span>
          <GameIcon class="chev" name="plus" :size="32" />
        </summary>
        <div class="answer" v-html="sanitizeHtml(f.answer)"></div>
      </details>
    </div>
  </SectionShell>
</template>

<style scoped>
.list {
  display: grid;
  gap: 10px;
  max-width: 820px;
  margin: 0 auto;
}

.q {
  padding: 4px 12px;
}

.ask {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  min-height: 56px;
  list-style: none;
  cursor: pointer;
  touch-action: manipulation;
}

.ask::-webkit-details-marker {
  display: none;
}

.ask .k-label {
  font-size: 1.05rem;
}

.chev {
  transition: transform 220ms var(--ease);
}

.q[open] .chev {
  transform: rotate(45deg);
}

.answer {
  padding: 4px 4px 14px 56px;
  font-family: var(--font-game-body);
  font-size: 0.95rem;
  line-height: 1.55;
  color: #dbe6ef;
}
</style>
